import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  ArrowLeft,
  Wallet,
  Truck,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

import { RootState } from "../../redux/store";
import ClassifiedOrders from "./ClassifiedOrders";
import ClassifiedDeliveries from "./ClassifiedDeliveries";

/* ─── PALETTE (mirrors GlamourGuestDashboard) ─── */
const G = {
  black:    "#000000",
  ink:      "#080808",
  dark:     "#0f0f0f",
  panel:    "#141414",
  surface:  "#1a1a1a",
  border:   "#1e1e1e",
  borderHi: "#2c2c2c",
  offwhite: "#ede8df",
  soft:     "#c8c4bc",
  muted:    "#4a4a4a",
  dim:      "#282828",
  gold:     "#a8894a",
  goldLt:   "#bfa06a",
  goldDim:  "#4a3c20",
  goldGlow: "rgba(168,137,74,0.07)",
  goldGlow2:"rgba(168,137,74,0.13)",
};

/* ─── FONTS ─── */
const GoogleFonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
    *, ::before, ::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${G.black}; }
    button, input { font-family: inherit; }
    ::selection { background: ${G.goldGlow2}; color: ${G.offwhite}; }
    ::-webkit-scrollbar { width: 3px; }
    ::-webkit-scrollbar-thumb { background: ${G.border}; }

    .mc-icon-btn {
      width: 36px; height: 36px;
      border: 1px solid ${G.border};
      background: transparent;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: all 0.18s;
      color: ${G.muted};
    }
    .mc-icon-btn:hover {
      border-color: ${G.goldDim};
      background: ${G.goldGlow};
      color: ${G.offwhite};
    }

    .mc-wallet-btn {
      width: 36px; height: 36px;
      border: 1px solid ${G.goldDim};
      background: ${G.goldGlow2};
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: all 0.18s;
      color: ${G.gold};
    }
    .mc-wallet-btn:hover {
      background: ${G.goldDim};
      color: ${G.offwhite};
    }

    .mc-tab-btn {
      flex: 1;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 4px; padding: 10px 4px;
      border: none; background: transparent;
      cursor: pointer; transition: all 0.18s;
      font-family: inherit;
      position: relative;
    }
  `}</style>
);

/* ─── HAIRLINE ─── */
const Hairline = () => (
  <div style={{
    height: 1,
    background: `linear-gradient(90deg, transparent, ${G.goldDim} 40%, ${G.goldDim} 60%, transparent)`,
  }} />
);

type TabId = "orders" | "delivered";

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: "orders",    label: "Orders",    icon: Truck        },
  { id: "delivered", label: "Delivered", icon: CheckCircle2 },
];

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const MetaClientDashboard: React.FC = () => {
  const navigate  = useNavigate();
  const user      = useSelector((s: RootState) => s.auth.user);
  const userId    = user?.userId || user?.uid;

  const [activeTab, setActiveTab] = useState<TabId>("orders");

  if (!userId) return null;

  return (
    <div style={{
      minHeight: "100vh",
      background: G.black,
      fontFamily: "'Montserrat', sans-serif",
      color: G.offwhite,
      display: "flex",
      flexDirection: "column",
    }}>
      <GoogleFonts />

      {/* ══ HEADER ══ */}
      <header style={{
        height: 52,
        background: "rgba(8,8,8,0.97)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${G.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 18px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        flexShrink: 0,
      }}>
        {/* Back */}
        <button className="mc-icon-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={15} strokeWidth={1.5} />
        </button>

        {/* Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={11} color={G.goldDim} strokeWidth={1.5} />
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 16,
            fontWeight: 500,
            color: G.offwhite,
            letterSpacing: 3,
            textTransform: "uppercase",
          }}>
            My Orders
          </h1>
          <Sparkles size={11} color={G.goldDim} strokeWidth={1.5} />
        </div>

        {/* Wallet */}
        <button
          className="mc-wallet-btn"
          onClick={() => navigate(`/PaymentMethods/${userId}`)}
        >
          <Wallet size={15} strokeWidth={1.5} />
        </button>
      </header>

      <Hairline />

      {/* ══ CONTENT ══ */}
      <main style={{
        flex: 1,
        padding: "20px 16px 88px 16px",
        overflowY: "auto",
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease: [0.2, 0, 0.1, 1] }}
          >
            {activeTab === "orders"    && <ClassifiedOrders    />}
            {activeTab === "delivered" && <ClassifiedDeliveries />}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* ══ BOTTOM TAB BAR ══ */}
      <div style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        background: "rgba(8,8,8,0.98)",
        backdropFilter: "blur(16px)",
        borderTop: `1px solid ${G.border}`,
        display: "flex",
        zIndex: 100,
        paddingBottom: "env(safe-area-inset-bottom, 8px)",
      }}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              className="mc-tab-btn"
              onClick={() => setActiveTab(id)}
              style={{ color: active ? G.gold : G.muted }}
            >
              {/* Active indicator */}
              {active && (
                <motion.div
                  layoutId="mc-tab-indicator"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 20,
                    height: 1,
                    background: G.gold,
                  }}
                />
              )}

              <Icon
                size={18}
                color={active ? G.gold : G.muted}
                strokeWidth={active ? 2 : 1.5}
              />
              <span style={{
                fontSize: 8,
                fontWeight: active ? 700 : 400,
                letterSpacing: 1.5,
                textTransform: "uppercase",
              }}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MetaClientDashboard;